
/**
 * Tiny formatting helpers reused across the UI. Pure functions so they
 * can be exercised by Vitest without rendering a single React tree.
 */

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Coarse "5 minutes ago" / "in 3 hours" formatter, in the portal's language. Unit thresholds
 * are picked so a stamp collapses to a stable string at a glance instead of a precise
 * wall-clock time; anything inside 45 seconds is "now".
 *
 * <p>Was English-only until 2026-09-17 (it served a notification bell that no longer calls
 * it). The stock popover prints it under a translated sentence, and "Sinhronizirano … 3 hours
 * ago" is not a translation. {@code Intl.RelativeTimeFormat} carries every locale we ship.
 */
export function formatRelative(input: Date | string | number, now: Date = new Date(), lang?: string): string {
  const t = input instanceof Date ? input.getTime() : new Date(input).getTime();
  if (Number.isNaN(t)) return "";
  const delta = t - now.getTime();
  const abs = Math.abs(delta);
  const sign = delta < 0 ? -1 : 1;

  const [n, unit]: [number, Intl.RelativeTimeFormatUnit] =
    abs < 45 * SECOND ? [0, "second"]
    : abs < HOUR ? [Math.round(abs / MINUTE), "minute"]
    : abs < DAY ? [Math.round(abs / HOUR), "hour"]
    : [Math.round(abs / DAY), "day"];
  // "auto" only at zero, where it yields "now"; past that it would turn 1 day into "yesterday",
  // which is a different claim from "24 hours ago" for a stamp that is about staleness.
  return new Intl.RelativeTimeFormat(resolveLocale(lang), { numeric: n === 0 ? "auto" : "always" })
    .format(sign * n, unit);
}

/**
 * Human-readable file size in IEC binary units. Used by the materials
 * list and the audit-log download size column.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KiB", "MiB", "GiB", "TiB"];
  let size = bytes / 1024;
  let unitIdx = 0;
  while (size >= 1024 && unitIdx < units.length - 1) {
    size /= 1024;
    unitIdx++;
  }
  return `${size.toFixed(size < 10 ? 1 : 0)} ${units[unitIdx]}`;
}

/**
 * Truncates a string to {@code max} characters and appends an ellipsis
 * when it had to cut. Preserves the original when it already fits so
 * callers can compare lengths to detect truncation.
 */
export function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, Math.max(0, max - 1)) + "…";
}


/**
 * Every locale this portal ships in writes the day before the month. Bare
 * {@code toLocaleDateString()} does not: it follows the BROWSER's locale, so an
 * IMP salesperson with an en-US browser read an order dated 5 November 2026 as
 * "5/11/2026" — which in that locale means 11 May. A date that flips meaning with
 * the reader's OS settings has no place on an order, a delivery date or an invoice.
 *
 * So the language the user picked in the portal decides the format, and "en" maps to
 * en-GB rather than en-US, because this is a European manufacturer selling into
 * European markets and every other locale here is day-first anyway.
 */
const DAY_FIRST_LOCALE: Record<string, string> = {
  en: "en-GB",
  sl: "sl-SI",
  de: "de-DE",
  it: "it-IT",
  fr: "fr-FR",
  es: "es-ES",
  ru: "ru-RU",
  tr: "tr-TR",
  hr: "hr-HR",
  sr: "sr-Latn-RS",
};

function resolveLocale(lang: string | undefined): string {
  // Passing a language keeps these functions pure and unit-testable. Omitting it is the
  // normal call: most date rendering happens in plain helpers and table cells where
  // threading i18n through would mean touching twenty call sites and their signatures.
  const chosen = lang ?? activeLanguage();
  if (!chosen) return "en-GB";
  return DAY_FIRST_LOCALE[chosen.split("-")[0].toLowerCase()] ?? "en-GB";
}

/**
 * The portal's currently selected language, read lazily so this module keeps working in
 * a unit test where i18next was never initialised.
 */
function activeLanguage(): string | undefined {
  try {
    const i18n = (globalThis as { __IMP_I18N__?: { resolvedLanguage?: string; language?: string } }).__IMP_I18N__;
    return i18n?.resolvedLanguage ?? i18n?.language;
  } catch {
    return undefined;
  }
}

/**
 * The BCP-47 locale the portal should format with right now. Exported so call sites that
 * build their own Intl formatter (currency with a per-row currency, custom digit rules)
 * get the same answer as {@link formatDate} instead of falling back to the browser.
 */
export function portalLocale(lang?: string): string {
  return resolveLocale(lang);
}

/** Date only, in the portal's selected language. Empty string for an unusable input. */
export function formatDate(input: Date | string | number | null | undefined, lang?: string): string {
  if (input === null || input === undefined || input === "") return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(resolveLocale(lang), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}

/**
 * Day and short month, with the year — "8 Sep 2026", "8. sep. 2026".
 *
 * <p>For the compact cells that used {@code month: "short"} directly. Keeping the shape and
 * changing only the locale: the screens that chose it did so to fit a column, and turning them
 * all into 08/09/2026 would be a redesign smuggled in under a bug fix.
 */
export function formatDateShort(input: Date | string | number | null | undefined, lang?: string): string {
  if (input === null || input === undefined || input === "") return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(resolveLocale(lang), {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
}

/** Day, full month and year — "8 September 2026". For a headline date, where it is the content. */
export function formatDateLong(input: Date | string | number | null | undefined, lang?: string): string {
  if (input === null || input === undefined || input === "") return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(resolveLocale(lang), {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
}

/** Day and short month, no year — for a column where the year is already established. */
export function formatDayMonth(input: Date | string | number | null | undefined, lang?: string): string {
  if (input === null || input === undefined || input === "") return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(resolveLocale(lang), { day: "numeric", month: "short" }).format(d);
}

/**
 * Time only, on a 24-hour clock.
 *
 * <p>{@code hour12: false} explicitly: every locale the portal ships reads 14:30, and leaving it
 * to the locale would give the one English reader "2:30 PM" in a column of 24-hour times.
 */
export function formatTime(input: Date | string | number | null | undefined, lang?: string): string {
  if (input === null || input === undefined || input === "") return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(resolveLocale(lang), {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

/** A weekday name, long ("Monday") or short ("Mon"). */
export function formatWeekday(
  input: Date | string | number | null | undefined,
  style: "long" | "short" = "long",
  lang?: string,
): string {
  if (input === null || input === undefined || input === "") return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(resolveLocale(lang), { weekday: style }).format(d);
}

/** Date and time, same locale rules as {@link formatDate}. */
export function formatDateTime(input: Date | string | number | null | undefined, lang?: string): string {
  if (input === null || input === undefined || input === "") return "";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(resolveLocale(lang), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}


/**
 * Money and plain numbers, in the portal's selected language — same reason as
 * {@link formatDate}. Intl.NumberFormat(portalLocale(), …) follows the BROWSER, so an order
 * worth 353.922,24 € rendered as "€353,922.24" for an en-US reader: the group and
 * decimal separators swap roles, which on a price list is not a cosmetic difference.
 *
 * Currency is deliberately NOT defaulted at the call site — a null amount renders as an
 * em dash rather than "0", because a missing price and a zero price mean different
 * things on an offer.
 */
export function formatMoney(
  amount: number | null | undefined,
  currency: string | null | undefined = "EUR",
  lang?: string,
): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return "—";
  return new Intl.NumberFormat(resolveLocale(lang), {
    style: "currency",
    currency: currency || "EUR",
  }).format(amount);
}

/** A duration in whole days, in the selected language — "54 days" / "54 dana" / "54 Tage".
 * CLDR handles the plural grammar, which flat i18n keys deliberately cannot. */
export function formatDays(days: number, lang?: string): string {
  return new Intl.NumberFormat(resolveLocale(lang), {
    style: "unit",
    unit: "day",
    unitDisplay: "long",
  }).format(days);
}

/** Plain number in the selected language; {@code digits} caps the fraction. */
export function formatNumber(
  value: number | null | undefined,
  digits = 0,
  lang?: string,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat(resolveLocale(lang), { maximumFractionDigits: digits }).format(value);
}
